"use client";

import React, { useEffect } from "react";
import { Stat, StatsProps } from "../types";
import Icon from "@/components/icons";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import Tooltip from "@/components/Tooltip";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import { useInView } from "react-intersection-observer";
import { backgroundColors } from "../colors";

const tooltipAllowedTags = ["b", "strong", "em", "i", "a", "p", "span"];

const Default = ({
  stats,
  background = "putty",
  trackEvents = false,
  maxStats = 3,
}: StatsProps) => {
  const { elementViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Component,
      text: "School Stats",
      htmlId: "stats",
      name: "stats",
      location: "degree-detail-page",
      position: "1",
    },
  });

  const { ref: contentRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (trackEvents && inView) {
      elementViewed();
    }
  }, [trackEvents, inView, elementViewed]);

  const backgroundColorClass = backgroundColors[background];
  const dividerColor = background === "putty" ? "bg-putty-400" : "bg-white";
  const textColor = background === "elm" ? "text-white" : "";
  return (
    <div
      ref={contentRef}
      className={`container flex flex-col md:flex-row justify-around items-start w-full gap-8 ${textColor} ${backgroundColorClass} py-12`}
    >
      {stats.map((stat: Stat, index: number) => (
        <React.Fragment key={`${stat.label}-${index}`}>
          <div
            className={`flex-1 flex flex-col py-6 md:py-0 w-full ${index >= maxStats ? "md:hidden" : ""}`}
          >
            <div className={`flex flex-col items-center`}>
              <div
                className={`flex gap-6 w-full md:w-auto ${stat.icon ? "items-start" : "items-center px-2 md:px-0"}`}
              >
                {stat.icon && (
                  <div className="flex-shrink-0">
                    <Icon
                      type={stat.icon}
                      svgProps={{ width: 56, height: 56 }}
                    />
                  </div>
                )}
                <div className="flex flex-col items-start w-full">
                  <div
                    className={`mb-2 text-4xl font-black italic leading-none ${stat.icon ? "text-left" : "text-center w-full"}`}
                  >
                    {stat.value}
                  </div>
                  <div className="text-center m-auto">
                    {sanitizeAndParseInlineHTML(
                      stat.label,
                      background === "elm",
                    )}
                    {stat.tooltip && (
                      <Tooltip
                        content={sanitizeAndParseInlineHTML(
                          stat.tooltip,
                          false,
                          tooltipAllowedTags,
                        )}
                        className="text-xs ml-[3px]"
                      >
                        <Icon
                          type="outline-warning-circle"
                          svgProps={{ width: 20, height: 20 }}
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Add divider between items but not after the last one */}
          {index < stats.length - 1 && (
            <div
              className={`self-center w-full h-px md:min-w-[1px] md:w-[1px] md:h-24 ${dividerColor} my-2 md:my-0 ${index >= maxStats - 1 ? "md:hidden" : ""} relative z-10`}
            ></div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default Default;
